import type { PropsWithChildren } from "react";
import type { FlexProps } from "@chakra-ui/react";
import {
	DrawerBody,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
	Flex,
	Icon,
	Text,
	UnorderedList,
	useMultiStyleConfig
} from "@chakra-ui/react";
import { IoCloseOutline } from "react-icons/io5";
import type { FundManagerListItemProps } from "@/components/FundManagerListItem/FundManagerListItem";
import { FundManagerListItem } from "@/components/FundManagerListItem/FundManagerListItem";
import { useInstanceConfig } from "@/services/InstanceConfigContext";
import { htmlify } from "@/services/utils";
import type { ShareClassTranslationMap } from "@/types/ShareClassTranslationMap";
import { AccessibleDrawer } from "../AccessibleDrawer/AccessibleDrawer";

export interface FundManagerDetailDrawerProps extends FlexProps {
	isOpen: boolean;
	onClose: () => void;
	heading?: string;
	fundManager?: FundManagerListItemProps;
	content?: string;
	translations?: ShareClassTranslationMap;
}

export const FundManagerDetailDrawer = ({
	heading = "Fund Manager",
	fundManager,
	isOpen,
	onClose,
	children,
	content,
	translations,
	...rest
}: PropsWithChildren<FundManagerDetailDrawerProps>) => {
	const styleParts = useMultiStyleConfig("FundManagerDetailDrawer");

	if (!fundManager) {
		return null;
	}

	return (
		<AccessibleDrawer
			data-testid="FundManagerDetailDrawer"
			id="fund-manager-details"
			isOpen={isOpen}
			onClose={onClose}
			size="md"
			{...rest}
		>
			<DrawerOverlay />
			<DrawerContent>
				<DrawerHeader sx={styleParts.header}>
					<Flex as="button" sx={styleParts.closeButton} aria-label="Close panel" onClick={onClose}>
						<Icon as={IoCloseOutline} />
					</Flex>
					<Text as="span" sx={styleParts.heading}>
						{htmlify(heading)}
					</Text>
				</DrawerHeader>

				<DrawerBody sx={styleParts.body}>
					<UnorderedList>
						<FundManagerListItem as="div" isOpen={false} {...fundManager} onClick={undefined} translations={translations} />
					</UnorderedList>
					<Text sx={styleParts.content}>{htmlify(content)}</Text>
					{children}
				</DrawerBody>
			</DrawerContent>
		</AccessibleDrawer>
	);
};
